<template>
  <section :id="idPrefix" class="support-faq" :aria-labelledby="`${idPrefix}-title`">
    <div class="support-faq__container">
      <h2 :id="`${idPrefix}-title`">{{ t('support.faq') }}</h2>

      <div class="faq-layout">
        <div class="faq-categories" role="tablist" :aria-label="t('support.faqCategories')">
          <button
            v-for="category in categories"
            :id="`${idPrefix}-tab-${category.id}`"
            :key="category.id"
            class="faq-category"
            :class="{ 'is-active': activeCategoryId === category.id }"
            type="button"
            role="tab"
            :aria-selected="activeCategoryId === category.id"
            :aria-controls="`${idPrefix}-panel-${category.id}`"
            @click="selectCategory(category.id)"
          >
            <span>{{ category.label }}</span>
          </button>
        </div>

        <div
          v-if="currentCategory"
          :id="`${idPrefix}-panel-${currentCategory.id}`"
          class="faq-list"
          role="tabpanel"
          :aria-labelledby="`${idPrefix}-tab-${currentCategory.id}`"
        >
          <article
            v-for="item in currentCategory.items"
            :key="item.id"
            class="faq-card"
            :class="{ 'is-open': isOpen(item.id) }"
          >
            <h3>
              <button
                class="faq-question"
                type="button"
                :aria-expanded="isOpen(item.id)"
                :aria-controls="`${idPrefix}-answer-${item.id}`"
                @click="toggle(item.id)"
              >
                <span>{{ item.question }}</span>
                <span class="faq-question__icon" aria-hidden="true">
                  {{ isOpen(item.id) ? '−' : '+' }}
                </span>
              </button>
            </h3>
            <div
              v-show="isOpen(item.id)"
              :id="`${idPrefix}-answer-${item.id}`"
              class="faq-answer"
              role="region"
            >
              <p>{{ item.answer }}</p>
            </div>
          </article>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
const { siteLanguageMixin } = require('@/utils/site-language.js')

export default {
  name: 'SupportFaq',
  mixins: [siteLanguageMixin],
  props: {
    categories: {
      type: Array,
      default: () => []
    },
    idPrefix: {
      type: String,
      default: 'support-faq'
    }
  },
  data() {
    return {
      activeCategoryId: null,
      openFaqIds: []
    }
  },
  computed: {
    currentCategory() {
      return this.categories.find((category) => category.id === this.activeCategoryId) || this.categories[0] || null
    }
  },
  watch: {
    categories: {
      immediate: true,
      handler(categories) {
        if (!categories.some((category) => category.id === this.activeCategoryId)) {
          this.activeCategoryId = categories.length ? categories[0].id : null
          this.openFaqIds = []
        }
      }
    }
  },
  methods: {
    selectCategory(id) {
      this.activeCategoryId = id
      this.openFaqIds = []
    },
    isOpen(id) {
      return this.openFaqIds.includes(id)
    },
    toggle(id) {
      this.openFaqIds = this.openFaqIds.includes(id)
        ? this.openFaqIds.filter((openId) => openId !== id)
        : [...this.openFaqIds, id]
    }
  }
}
</script>

<style scoped>
.support-faq {
  --support-accent: #ef0a6a;
  min-height: 733px;
  padding: 80px 0;
  background: #f5f5f7;
  box-sizing: border-box;
}

.support-faq__container {
  width: min(1296px, calc(100% - 48px));
  margin: 0 auto;
}

.support-faq h2,
.faq-card h3,
.faq-answer p {
  margin: 0;
}

.support-faq h2 {
  font-size: 40px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
}

.faq-layout {
  display: grid;
  grid-template-columns: 280px minmax(0, 990px);
  gap: 26px;
  margin-top: 52px;
}

.faq-categories {
  min-height: 473px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding: 0;
  border-right: 1px solid rgba(0, 0, 0, 0.2);
}

.faq-category,
.faq-question {
  font: inherit;
}

.faq-category {
  position: relative;
  width: 100%;
  min-height: 48px;
  padding: 10px 24px 10px 0;
  border: 0;
  color: rgba(0, 0, 0, 0.5);
  background: transparent;
  font-size: 20px;
  line-height: 28px;
  text-align: left;
  text-transform: capitalize;
  cursor: pointer;
}

.faq-category:hover {
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.04);
}

.faq-category::after {
  content: '';
  position: absolute;
  top: 12px;
  right: -2px;
  width: 4px;
  height: 24px;
  background: transparent;
}

.faq-category.is-active {
  padding-left: 0;
  color: var(--support-accent);
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  font-weight: 700;
}

.faq-category.is-active::after {
  top: 12px;
  right: -2px;
  bottom: auto;
  left: auto;
  width: 3px;
  height: 24px;
  background: var(--support-accent);
  border-radius: 3px;
}

.faq-category:focus {
  outline: none;
}

.faq-question:focus {
  outline: none;
}

.faq-category:focus-visible,
.faq-question:focus-visible {
  outline: 2px solid var(--support-accent);
  outline-offset: 3px;
}

.faq-list {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 24px;
}

.faq-card {
  overflow: hidden;
  border-radius: 12px;
  background: #fff;
}

.faq-question {
  width: 100%;
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 18px 24px;
  border: 0;
  color: rgba(0, 0, 0, 0.8);
  background: #fff;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  text-align: left;
  cursor: pointer;
}

.faq-question__icon {
  width: 32px;
  height: 32px;
  display: grid;
  flex: 0 0 32px;
  place-items: center;
  color: #000;
  font-size: 25px;
  font-weight: 400;
  line-height: 1;
}

.faq-card:not(.is-open) .faq-question:hover,
.faq-card:not(.is-open) .faq-question:focus-visible {
  color: rgba(0, 0, 0, 0.4);
}

.faq-card:not(.is-open) .faq-question:hover .faq-question__icon,
.faq-card:not(.is-open) .faq-question:focus-visible .faq-question__icon {
  color: var(--support-accent);
}

.faq-answer {
  margin: 0 24px;
  padding: 13px 0 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.2);
}

.faq-answer p {
  color: rgba(0, 0, 0, 0.8);
  font-size: 16px;
  font-weight: 500;
  line-height: 1.4;
  white-space: pre-line;
}

@media (max-width: 1100px) {
  .faq-layout {
    grid-template-columns: 220px minmax(0, 1fr);
  }
}

@media (max-width: 700px) {
  .support-faq {
    min-height: 804px;
    padding: 56px 0 64px;
  }

  .support-faq__container {
    width: min(400px, calc(100vw - 32px));
    margin: 0 0 0 16px;
  }

  .support-faq h2 {
    font-size: 28px;
  }

  .faq-layout {
    display: block;
    margin-top: 35px;
  }

  .faq-categories {
    min-height: 35px;
    height: 35px;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 24px;
    padding: 0 0 2px;
    overflow-x: auto;
    overflow-y: hidden;
    border: 0;
    scrollbar-width: none;
  }

  .faq-categories::-webkit-scrollbar {
    display: none;
  }

  .faq-category {
    width: auto;
    min-width: max-content;
    min-height: 33px;
    flex: 0 0 auto;
    padding: 0;
    border-radius: 0;
    background: transparent;
    font-size: 16px;
    line-height: 20px;
  }

  .faq-category::after {
    top: auto;
    right: 0;
    bottom: 0;
    left: 0;
    width: auto;
    height: 2px;
  }

  .faq-category.is-active {
    padding-left: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
  }

  .faq-category.is-active::after {
    top: auto;
    right: 0;
    bottom: 0;
    left: 0;
    width: auto;
    height: 2px;
    border-radius: 0;
  }

  .faq-list {
    gap: 24px;
    margin-top: 54px;
  }

  .faq-question {
    min-height: 72px;
    gap: 24px;
    padding: 18px 24px;
    font-size: 16px;
  }

  .faq-question__icon {
    width: 32px;
    height: 32px;
    flex-basis: 32px;
  }

  .faq-answer {
    margin: 0 24px;
    padding: 16px 0 24px;
  }

  .faq-answer p {
    font-size: 16px;
    line-height: 1.4;
  }
}

@media (max-width: 390px) {
  .support-faq__container {
    width: min(400px, calc(100vw - 32px));
  }
}
</style>
